import type { RumInitConfiguration } from '@datadog/browser-rum';
import { DatadogClass, JSONValue, TDataDogCommonConfig } from './DatadogClass';
import { APP_VERSION } from 'src/app/settings';

type DatadogRum = typeof import('@datadog/browser-rum')['datadogRum'];

class Datadog extends DatadogClass {
  private _rum: DatadogRum | undefined;

  public async initDadDog({
    trackUserInteractions,
    trackResources,
    sessionSampleRate,
    environment,
    applicationId,
    clientToken,
    beforeSend,
  }: TDataDogCommonConfig) {
    const { datadogRum } = await import('@datadog/browser-rum');
    this._rum = datadogRum;

    return Promise.resolve(
      // https://docs.datadoghq.com/real_user_monitoring/browser/setup/client#configuration
      datadogRum.init({
        applicationId,
        clientToken,
        site: 'datadoghq.com',
        service: 'web-photosi-editor',
        env: environment,
        sessionSampleRate,
        sessionReplaySampleRate: 0,
        trackUserInteractions,
        trackResources,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
        version: `${APP_VERSION}${process.env.SENTRY_DIST}`,
        beforeSend,
      } satisfies RumInitConfiguration),
    );
  }

  public async setUserInfo(userId: string, userType: string): Promise<void> {
    if (this.isDatadogInitialized) {
      this._rum?.setUser({
        id: userId,
        type: userType,
      });
    }
    return void 0;
  }

  public async clearUserInfo(): Promise<void> {
    if (this.isDatadogInitialized) {
      this._rum?.clearUser();
    }
    return void 0;
  }

  public startMeasure(operationName: string) {
    if (__DEV__) {
      console.log(`[MEASURE][${operationName}] startMeasure`, new Date());
    }
    this.performingActions[operationName] = {
      startTime: performance.now(),
    };
  }

  public endMeasure(operationName: string, otherParams = {}) {
    if (operationName in this.performingActions && 'startTime' in this.performingActions[operationName]) {
      const endTime = performance.now();
      this.performingActions[operationName] = {
        ...this.performingActions[operationName],
        endTime,
      };

      const { startTime } = this.performingActions[operationName];
      const duration = Math.ceil(endTime - startTime);
      const ddActionParams = {
        ...otherParams,
        duration,
      };
      this._rum?.addAction(operationName, ddActionParams);

      if (__DEV__) {
        console.log(`[MEASURE][${operationName}] endMeasure`, new Date(), '\n', ddActionParams);
      }
      this.performedActionList.push({
        ...ddActionParams,
        name: operationName,
        logTime: new Date(),
      });

      delete this.performingActions[operationName];
    }
  }

  public cleanMeasure(operationName: string) {
    if (__DEV__) {
      console.log(`[MEASURE][${operationName}] cleanMeasure`, new Date());
    }
    delete this.performingActions[operationName];
  }

  public recordMeasure(operationName: string, durationMs: number, otherParams = {}) {
    const ddActionParams = {
      ...otherParams,
      duration: durationMs,
    };
    this._rum?.addAction(operationName, ddActionParams);

    if (__DEV__) {
      console.log(`[MEASURE][${operationName}] recordMeasure`, new Date(), '\n', ddActionParams);
    }
    this.performedActionList.push({
      ...ddActionParams,
      name: operationName,
      logTime: new Date(),
    });
  }

  public setDatadogFeatureFlag(featureFlag: string, featureFlagValue: string): void {
    if (this.isDatadogInitialized) {
      this._rum?.addFeatureFlagEvaluation(featureFlag, featureFlagValue);
    }
    return void 0;
  }

  public async logError(...args: any): Promise<void> {
    if (this.isDatadogInitialized) {
      const error = args
        .map((argument: any) => {
          return typeof argument === 'object' ? JSON.stringify(argument) : argument;
        })
        .join(' ');
      this._rum?.addError(new Error(error));
    }
    return void 0;
  }

  public async logInfo(key: string, context?: any): Promise<void> {
    if (this.isDatadogInitialized) {
      this._rum?.addAction(key, context);
    }
    return void 0;
  }

  public async setGlobalAttribute(scopeKey: string, value: JSONValue): Promise<void> {
    if (this.isDatadogInitialized) {
      this._rum?.setGlobalContextProperty(scopeKey, value);
    }
    return void 0;
  }

  public trackViews(): void {
    // Datadog tracking is supported by default in web. Add platform conditioning around this method to avoid this warning
    return void 0;
  }
}

const DatadogObj = new Datadog();

// @ts-ignore
window['Datadog'] = DatadogObj;

export default DatadogObj;
